// src/pages/TopDomains.jsx 
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  CartesianGrid,
} from "recharts";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { request } from "../../utils/request";
import { motion } from "framer-motion";
import { CopyToClipboard } from "react-copy-to-clipboard";
import "./dashboard.scss";

/* ---------- Utilities ---------- */
function normalizeArrayResp(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.data.docs)) return resp.data.docs;
  }
  if (Array.isArray(resp.docs)) return resp.docs;
  const firstArray = Object.values(resp).find((v) => Array.isArray(v));
  return Array.isArray(firstArray) ? firstArray : [];
}

const extractDomain = (log) => {
  try {
    if (!log) return null;
    const ed = log.eventData || {};
    if (ed.history && typeof ed.history === "object") {
      if (ed.history.domain) return String(ed.history.domain).toLowerCase();
      if (ed.history.url) {
        try { return new URL(ed.history.url).hostname.toLowerCase(); } catch {
          const m = String(ed.history.url).match(/https?:\/\/([^\/]+)/i);
          if (m && m[1]) return m[1].toLowerCase();
        }
      }
    }
    if (ed.domain) return String(ed.domain).toLowerCase();
    if (log.domain) return String(log.domain).toLowerCase();
    if (ed.data && ed.data.domain) return String(ed.data.domain).toLowerCase();
    if (ed.payload && ed.payload.domain) return String(ed.payload.domain).toLowerCase();
  } catch (e) {}
  return null;
}

const isIpAddress = (value) => {
  if (!value) return false;
  const v = String(value).trim();
  if (/^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/.test(v)) {
    return true;
  }
  if (v.includes(":") && /^[0-9a-f:]+$/i.test(v)) {
    return true;
  }
  return false;
};

const shortName = (domain) => {
  if (!domain) return "";
  let d = String(domain).trim().toLowerCase().replace(/:\d+$/, "").replace(/^www\./, "");

  if (isIpAddress(d)) return d;

  d = d.replace(/(\.co\.uk|\.com|\.org|\.net|\.io|\.co|\.in|\.dev|\.app|\.gov|\.edu|\.info|\.biz)$/i, "");
  const parts = d.split(".");
  return parts[0] || d;
};

const truncateLabel = (str, maxLen = 12) => {
  if (!str) return "";
  return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
};

function lightenHex(hex, percent) {
  try {
    const c = hex.replace("#", "");
    const num = parseInt(c, 16);
    const r = Math.min(255, (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100)));
    const g = Math.min(255, ((num >> 8) & 0x00ff) + Math.round((255 - ((num >> 8) & 0x00ff)) * (percent / 100)));
    const b = Math.min(255, (num & 0x0000ff) + Math.round((255 - (num & 0x0000ff)) * (percent / 100)));
    const out = (r << 16) | (g << 8) | b;
    return `#${out.toString(16).padStart(6, "0")}`;
  } catch {
    return hex;
  }
}

const PALETTE = [
  "#0B2545",
  "#1F6FEB",
  "#6299F1",
  "#9ABEF6",
  "#C7DBFB",
  "#6B7A90",
];

/* ---------- Custom YAxis Tick ---------- */
const CustomYAxisTick = ({ x, y, payload, maxChars = 11 }) => {
  const rawLabel = String(payload.value || "");
  const label = isIpAddress(rawLabel) ? rawLabel : truncateLabel(rawLabel, maxChars);

  return (
    <text
      x={4}
      y={y}
      dy={4}
      textAnchor="start"
      fontSize={14} // Fixed CSS Size
      fontFamily="Calibri, Arial, sans-serif"
      fontWeight={600}
      fill="var(--vault-navy)"
    >
      {label}
    </text>
  );
};

/* ---------- Custom Bar Label ---------- */
const CustomBarLabel = (props) => {
  const { x, y, width, height, value } = props;
  if (!value) return null;

  const text = String(value);
  const textPx = text.length * 8 + 8;
  const insideThreshold = textPx + 12;

  if (width >= insideThreshold) {
    return (
      <text
        x={x + width - 8}
        y={y + height / 2}
        dy={4}
        textAnchor="end"
        fontSize={13} // Fixed CSS Size
        fontFamily="Arial, sans-serif"
        fontWeight={700}
        fill="#ffffff"
      >
        {text}
      </text>
    );
  }

  return (
    <text
      x={x + width + 5}
      y={y + height / 2}
      dy={4}
      textAnchor="start"
      fontSize={13} // Fixed CSS Size
      fontFamily="Arial, sans-serif"
      fontWeight={700}
      fill="var(--vault-navy)"
    >
      {text}
    </text>
  );
};

/* ---------- Custom Tooltip ---------- */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  
  return (
    <div
      style={{
        background: "var(--canvas)",
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 13,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        maxWidth: 220,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 3, color: "var(--vault-navy)", wordBreak: "break-all" }}>
        {d.domain}
      </div>
      <div className="text-secondary">
        <span style={{ fontWeight: 700, color: "var(--vault-navy)" }}>{d.count}</span> visits
        {d.uniqueUsers > 0 && (
          <span> · {d.uniqueUsers} user{d.uniqueUsers !== 1 ? "s" : ""}</span>
        )}
      </div>
    </div>
  );
};

/* ---------- Component ---------- */
export default function TopAccessedDomains() {
  const [loading, setLoading] = useState(false);
  const [topDomains, setTopDomains] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [totalUniqueDomains, setTotalUniqueDomains] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const copyTimer = useRef(null);
  const fetchedOnce = useRef(false);

  const fetchTopDomains = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await request(`logs?page=1&limit=20000&eventType=history`, { method: "GET" }).catch(async () => {
        return await request(`logs?page=1&limit=20000`, { method: "GET" });
      });

      const logs = normalizeArrayResp(resp);
      const map = new Map();

      for (const lg of logs) {
        if (!lg) continue;
        const type = (lg.eventType || lg.type || "").toString().toLowerCase();
        if (type && type !== "history" && type !== "historyvisit" && type !== "webhistory") continue;

        const domain = extractDomain(lg);
        if (!domain) continue;
        const key = domain;

        if (!map.has(key)) {
          map.set(key, { domain: key, count: 0, firstSeen: null, lastSeen: null, sampleUrls: new Set(), users: new Set() });
        }
        const e = map.get(key);
        e.count += 1;

        const ts = lg.timestamp || lg.createdAt || (lg.eventData && lg.eventData.data && lg.eventData.data.timestamp) || null;
        const dt = ts ? new Date(ts) : null;
        if (dt && !isNaN(dt.getTime())) {
          if (!e.firstSeen || dt < e.firstSeen) e.firstSeen = dt;
          if (!e.lastSeen || dt > e.lastSeen) e.lastSeen = dt;
        }

        const url = lg?.eventData?.history?.url || lg?.eventData?.data?.url || lg?.eventData?.payload?.url || null;
        if (url && e.sampleUrls.size < 6) e.sampleUrls.add(url);

        const uid = lg.userId || lg.user || lg.user_id || null;
        if (uid) e.users.add(String(uid));
      }

      setTotalUniqueDomains(map.size);

      const arr = Array.from(map.values()).map((e) => ({
        domain: e.domain,
        short: shortName(e.domain),
        count: e.count,
        firstSeen: e.firstSeen,
        lastSeen: e.lastSeen,
        sampleUrls: Array.from(e.sampleUrls).slice(0, 5),
        uniqueUsers: e.users.size,
      }));

      arr.sort((a, b) => b.count - a.count);
      setTopDomains(arr.slice(0, 5));
      setFetchedAt(new Date());
    } catch (err) {
      console.error("TopAccessedDomains fetch error:", err);
      setTopDomains([]);
      setTotalUniqueDomains(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;
    fetchTopDomains();
    return () => { if (copyTimer.current) clearTimeout(copyTimer.current); };
  }, [fetchTopDomains]);

  const openDetails = (d) => { setSelected(d); setCopied(false); };
  const closeModal = () => setSelected(null);

  const handleCopy = () => {
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1700);
  };

  const totalVisits = topDomains.reduce((s, t) => s + (t.count || 0), 0);

  const chartData = topDomains.map((t, idx) => ({
    ...t,
    label: isIpAddress(shortName(t.domain)) ? shortName(t.domain) : truncateLabel(shortName(t.domain), 11),
    color: PALETTE[idx % PALETTE.length],
    gradient: lightenHex(PALETTE[idx % PALETTE.length], 22),
  }));

  const barMaxValue = chartData.length ? Math.max(...chartData.map((c) => c.count)) : 1;

  const Y_AXIS_WIDTH = 110; // Slightly widened to accommodate 14px font
  const RIGHT_MARGIN = 38;

  return (
    <div className="widget-card" style={{ width: "100%", height: "100%" }}>
      <div 
        className="global-card"
        style={{
          height: "100%",
          padding: "1.25rem", // Standard padding
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden" // Keeps internal content cleanly clipped
        }}
      >
        {/* Toast Notification */}
        {copied && (
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 90,
              padding: "8px 12px",
              borderRadius: 8,
              fontWeight: 700,
              background: "var(--vault-navy)",
              color: "var(--canvas)",
              boxShadow: "0 8px 26px rgba(0,0,0,0.2)"
            }}
          >
            Copied ✓
          </div>
        )}

        {/* Header Area */}
        <div style={{ paddingBottom: "12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h5 className="global-section-title" style={{ margin: 0 }}>
              Top Accessed Domains / Apps
            </h5>
            <div className="global-caption text-secondary" style={{ marginTop: 4 }}>
              Top 5 visited domains
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexShrink: 0 }}>
            {/* Stat 1 — Total visits */}
            <div className="bg-mist" style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: "5px 12px", borderRadius: 8, minWidth: 70, border: "1px solid var(--hairline)"
            }}>
              <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1, color: "var(--vault-navy)" }}>
                {totalVisits.toLocaleString()}
              </span>
              <span className="text-secondary" style={{ fontSize: 10, marginTop: 3, whiteSpace: "nowrap" }}>
                Total visits
              </span>
            </div>

            {/* Stat 2 — Unique domains */}
            <div className="bg-mist" style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: "5px 12px", borderRadius: 8, minWidth: 70, border: "1px solid var(--hairline)"
            }}>
              <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1, color: "var(--vault-navy)" }}>
                {totalUniqueDomains.toLocaleString()}
              </span>
              <span className="text-secondary" style={{ fontSize: 10, marginTop: 3, whiteSpace: "nowrap" }}>
                Unique domains
              </span>
            </div>
          </div>
        </div>

        {/* Chart Area */}
        <motion.div whileHover={{ y: -2 }} style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}>
            {loading ? (
              <div className="text-secondary" style={{ fontWeight: 600 }}>Calculating...</div>
            ) : chartData.length === 0 ? (
              <div className="text-secondary">No domain history found</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={chartData}
                  margin={{ top: 4, right: RIGHT_MARGIN, left: 0, bottom: 4 }}
                  onMouseLeave={() => setHoverIndex(-1)}
                >
                  <defs>
                    {chartData.map((c, i) => (
                      <linearGradient id={`grad-td-${i}`} key={`g-${i}`} x1="0" x2="1">
                        <stop offset="0%" stopColor={c.color} stopOpacity="1" />
                        <stop offset="100%" stopColor={c.gradient} stopOpacity="1" />
                      </linearGradient>
                    ))}
                  </defs>

                  <CartesianGrid stroke="var(--hairline)" vertical={false} />

                  <XAxis
                    type="number"
                    tickLine={false}
                    axisLine={false}
                    hide
                  />

                  <YAxis
                    dataKey="label"
                    type="category"
                    width={Y_AXIS_WIDTH}
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    tick={(props) => (
                      <CustomYAxisTick {...props} maxChars={11} />
                    )}
                  />

                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "var(--mist)", opacity: 0.5 }}
                  />

                  <Bar
                    dataKey="count"
                    barSize={20}
                    radius={[0, 4, 4, 0]}
                    onMouseEnter={(_, idx) => setHoverIndex(idx)}
                    onClick={(data, idx) => openDetails(chartData[idx])}
                    style={{ cursor: "pointer" }}
                    label={(props) => (
                      <CustomBarLabel
                        {...props}
                        barMaxValue={barMaxValue}
                      />
                    )}
                  >
                    {chartData.map((entry, index) => {
                      const opacity = hoverIndex === -1 ? 1 : hoverIndex === index ? 1 : 0.4;
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={`url(#grad-td-${index})`}
                          fillOpacity={opacity}
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Footer Metadata */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, flexShrink: 0 }}>
            <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
              Showing top {chartData.length} domains
            </div>
            <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
              {fetchedAt ? `Updated ${fetchedAt.toLocaleTimeString()}` : ""}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Detail Modal (Using MUI Dialog for consistency) */}
      <Dialog
        open={!!selected}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "global-card",
          sx: { minHeight: "auto", p: 0, overflow: "hidden" } // Standardized to match other dialogs
        }}
      >
        {selected && (
          <>
            <DialogTitle className="global-dialog-title" sx={{ textAlign: "left", p: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
              <div style={{ minWidth: 0 }}>
                <span style={{ wordBreak: "break-all" }}>{selected.domain}</span>
                <div className="text-secondary" style={{ marginTop: 6, fontSize: "14px", fontWeight: 400 }}>
                  <strong style={{ color: "var(--vault-navy)" }}>{selected.count}</strong> visits &nbsp;·&nbsp; {selected.uniqueUsers ?? 0} unique user{selected.uniqueUsers !== 1 ? "s" : ""}
                </div>
              </div>
              <CopyToClipboard text={selected.domain} onCopy={handleCopy}>
                <Button className="global-button global-button-primary" size="small" sx={{ flexShrink: 0 }}>
                  Copy
                </Button>
              </CopyToClipboard>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: "var(--hairline)", p: 3 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--vault-navy)", marginBottom: 4 }}>First seen</div>
                  <div className="text-secondary" style={{ fontSize: 14 }}>
                    {selected.firstSeen ? new Date(selected.firstSeen).toLocaleString() : "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--vault-navy)", marginBottom: 4 }}>Last seen</div>
                  <div className="text-secondary" style={{ fontSize: 14 }}>
                    {selected.lastSeen ? new Date(selected.lastSeen).toLocaleString() : "—"}
                  </div>
                </div>
              </div>

              {selected.sampleUrls && selected.sampleUrls.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--vault-navy)", marginBottom: 8 }}>Sample URLs</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {selected.sampleUrls.map((s, i) => (
                      <a
                        key={i}
                        href={s}
                        target="_blank"
                        rel="noreferrer"
                        className="text-interactive"
                        style={{ fontSize: 14, wordBreak: "break-all", textDecoration: "underline" }}
                      >
                        {s}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: "1px solid var(--hairline)" }}>
              <Button onClick={closeModal} className="global-button global-button-text">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
}